'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useParams } from 'next/navigation';

import { getPrizeEntryApi, postDrawsApi } from '@flipster/apis/v1/campaign';
import { PrizeEntryItem } from '@flipster/apis/v1/campaign/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { ServerError } from '@flipster/utils/constants/server-error';
import { numberFormatter } from '@flipster/utils/num';

import { MaxWidthSection } from '@web/app/[locale]/(web)/campaign/_section/_layout';
import { contentfulImageUrl } from '@web/app/[locale]/(web)/campaign/_section/misc/assets';
import { RegisterButton } from '@web/app/[locale]/(web)/campaign/_section/misc/campaign-button';
import { ImgOrLottie } from '@web/app/[locale]/(web)/campaign/_section/misc/img-or-lottie';
import { HallOfFame } from '@web/app/[locale]/(web)/campaign/_section/prize-entry/hall-of-fame';
import { SubmitPrizeEntryModal } from '@web/app/[locale]/(web)/campaign/_section/prize-entry/submit-prize-entry-modal';
import { DrawSectionItemFragment } from '@web/app/[locale]/(web)/campaign/query.generated';

interface PrizeEntryProps {
  layout: DrawSectionItemFragment;
}

export const PrizeEntry = ({ layout }: PrizeEntryProps) => {
  const router = useRouter();
  const { slug } = useParams<{ slug: string }>();

  const overlay = useOverlay();
  const { isLoggedInAtom, isCookieLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);

  const { data: serverData, refetch } = useQuery({
    queryKey: ['getPrizeEntryApi', slug, isCookieLoggedIn],
    queryFn: () => getPrizeEntryApi(slug!),
    select: (res) => res.data,
  });

  const registerMutation = useMutation({
    mutationFn: () => postDrawsApi(slug),
    onSuccess() {
      snackbar.success({ message: 'You are registered to the event' });
      refetch();
    },
    onError(error) {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
  });

  const isRegistered = serverData?.isRegistered;
  const drawStatus = serverData?.drawStatus ?? 'UPCOMING';
  const myTicket = serverData?.myTicket;
  const prizeEntries = serverData?.items;
  const availableTickets =
    isLoggedIn && drawStatus !== 'UPCOMING' ? Math.max(0, (myTicket?.total ?? 0) - (myTicket?.used ?? 0)) : undefined;

  if (!layout) return null;

  const isFinished = drawStatus === 'FINISHED';

  const openSubmitPrizeEntryModal = (item: PrizeEntryItem) => {
    if (!isLoggedIn) {
      router.push('/signup');
      return;
    }
    overlay.open(({ isOpen, onClose, unmount }) => (
      <SubmitPrizeEntryModal
        isOpen={isOpen}
        onClose={onClose}
        item={item}
        availableTickets={availableTickets ?? 0}
        onExit={unmount}
      />
    ));
  };

  return (
    <>
      <MaxWidthSection sectionType='draw' css={vstack.raw({ pt: { base: 16, tablet: 32 } })}>
        <RegisterButton
          isRegistered={isRegistered}
          isFinished={drawStatus === 'FINISHED'}
          handleRegister={() => registerMutation.mutateAsync()}
        />
        <div
          className={css({
            w: { base: '100%', tablet: 590 },
            maxW: 590,
            mt: { base: 64, tablet: 80 },
            mb: 40,
          })}
        >
          <h2
            className={css({
              textStyle: 'Display/XL',
              color: 'content.default.level-5',
              textAlign: 'center',
            })}
          >
            {layout.title}
          </h2>
          <div
            className={css({
              textStyle: 'Title/S',
              color: 'content.default.level-5',
              textAlign: 'center',
              mt: 12,
            })}
          >
            {layout.subTitle}
          </div>
          <div
            className={hstack({
              w: '100%',
              p: 16,
              rounded: 8,
              textStyle: 'Title/S',
              bgColor: 'background.default.level-3',
              justify: 'space-between',
              mt: 24,
            })}
          >
            <p className={css({ flex: 2, color: 'content.accent.promotion' })}>
              <FormattedMessage id='Campaign.r251127.Your_available_draw_entries' />
            </p>
            <p className={css({ flex: 1, textAlign: 'right' })}>
              <Formatted.Number value={availableTickets} />
            </p>
          </div>
          <p
            className={css({
              textStyle: 'Title/S',
              color: isFinished ? 'content.state.warning' : 'content.default.level-5',
              textAlign: 'center',
              mt: 24,
              whiteSpace: 'pre-line',
            })}
          >
            {isFinished ? <FormattedMessage id='Campaign.r251127.Draw_Event_Ended_Text' /> : layout.description}
          </p>
        </div>
        {!!prizeEntries && (
          <div
            className={css({
              w: '100%',
              p: { tablet: 20 },
              rounded: 40,
              bgColor: 'background.accent.primary',
            })}
          >
            <ul
              className={flex({
                gap: { base: 12, tablet: 24 },
                p: { tablet: 20 },
                flexWrap: 'wrap',
                justifyContent: 'center',
                rounded: 28,
                bgColor: { base: 'background.default.level-0', tablet: 'background.default.level-1' },
              })}
            >
              {prizeEntries.map((item) => (
                <li
                  key={item.id}
                  className={vstack({
                    flex: { base: '0 1 100%', tablet: '0 1 calc((100% - 2 * 24px) / 3)' },
                    textStyle: 'Title/XS',
                    textAlign: 'center',
                    gap: 12,
                    py: 24,
                    px: 16,
                    rounded: 28,
                    bgColor: 'background.default.level-3',
                  })}
                >
                  <div className={vstack({ gap: 8, minH: { base: 87, tablet: 92 } })}>
                    <p
                      className={hstack({
                        textStyle: 'Display/L',
                        color: 'content.accent.promotion',
                        gap: 6,
                      })}
                    >
                      {item.name}
                      <Badge type='tint' color='promotion'>{`x${item.prizeCount}`}</Badge>
                    </p>
                    {item.description && (
                      <p className={css({ color: 'content.accent.promotion' })}>{item.description}</p>
                    )}
                    <span className={css({ color: 'content.default.level-5' })}>
                      <FormattedMessage id='Campaign.r251127.Total_entries' />
                      {': '}
                      {numberFormatter(item.totalEntries)}
                    </span>
                  </div>
                  <div
                    className={css({
                      mx: 14,
                      aspectRatio: '1 / 1',
                      maxW: { base: 120, tablet: 300 },
                      maxH: { base: 120, tablet: 300 },
                    })}
                  >
                    <ImgOrLottie url={contentfulImageUrl(item.image)} alt={item.name} css={{ w: '100%' }} />
                  </div>
                  <span className={css({ color: 'content.default.level-5' })}>
                    <FormattedMessage id='Campaign.r251127.Your_draw_entries' />
                    {': '}
                    {isLoggedIn ? numberFormatter(item.yourEntries ?? 0) : '-'}
                  </span>
                  <Button
                    hierarchy='primary'
                    size='large'
                    color='neutral'
                    label={<FormattedMessage id='Campaign.r251127.Submit_entry' />}
                    onClick={() => openSubmitPrizeEntryModal(item)}
                    disabled={drawStatus !== 'ONGOING'}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </MaxWidthSection>
      {/*임시 3주뒤 삭제*/}
      {slug === 'born-to-be-a-trader' && (
        <HallOfFame
          tabs={[
            { value: 'volume', id: 'born-to-be-a-trader-volume' },
            { value: 'pnl', id: 'born-to-be-a-trader-pnl' },
          ]}
        />
      )}
    </>
  );
};
